import { useAuth } from '../contexts/AuthContext';
import { getAccessibleRoutes, checkRolePermission } from '../utils/apiProtection';

/**
 * Hook for role-based access control
 * Provides utilities for checking user permissions and access
 */
export const useRoleAccess = () => {
  const { user, userProfile, isAdmin, isTenant, hasRole } = useAuth();

  // Get user role
  const userRole = userProfile?.role || user?.user_metadata?.role;

  /**
   * Check if user has specific role
   * @param {string} role - Role to check
   * @returns {boolean}
   */
  const hasRoleAccess = (role) => {
    return hasRole(role);
  };

  /**
   * Check if user can access specific route
   * @param {string} route - Route path
   * @returns {boolean}
   */
  const canAccessRoute = (route) => {
    if (!userRole) return false;
    
    const accessibleRoutes = getAccessibleRoutes(userRole);
    return accessibleRoutes.includes(route);
  };

  /**
   * Check if user can perform specific action
   * @param {string} action - Action to check
   * @returns {boolean}
   */
  const canPerformAction = (action) => {
    if (!userRole) return false;

    const actionPermissions = {
      admin: ['create', 'read', 'update', 'delete', 'manage_users', 'manage_settings'],
      manager: ['create', 'read', 'update', 'delete'],
      tenant: ['read', 'update_own']
    };

    return actionPermissions[userRole]?.includes(action) || false;
  };

  /**
   * Check if user can access admin features
   * @returns {boolean}
   */
  const canAccessAdmin = () => {
    return isAdmin();
  };

  /**
   * Check if user can access tenant features
   * @returns {boolean}
   */
  const canAccessTenant = () => {
    return isTenant();
  };

  /**
   * Check if user can manage settings
   * @returns {boolean}
   */
  const canManageSettings = () => {
    return userRole === 'admin';
  };

  /**
   * Check if user can manage users
   * @returns {boolean}
   */
  const canManageUsers = () => {
    return userRole === 'admin';
  };

  /**
   * Check if user can delete resources
   * @returns {boolean}
   */
  const canDelete = () => {
    return userRole === 'admin' || userRole === 'manager';
  };

  /**
   * Check if user can create resources
   * @returns {boolean}
   */
  const canCreate = () => {
    return userRole === 'admin' || userRole === 'manager';
  };

  /**
   * Check if user can update resources
   * @returns {boolean}
   */
  const canUpdate = () => {
    return userRole === 'admin' || userRole === 'manager' || userRole === 'tenant';
  };

  /**
   * Check if user can read resources
   * @returns {boolean}
   */
  const canRead = () => {
    return true; // All authenticated users can read
  };

  /**
   * Get user's accessible routes
   * @returns {Array<string>}
   */
  const getAccessibleRoutes = () => {
    return getAccessibleRoutes(userRole);
  };

  /**
   * Check if current route is accessible
   * @param {string} currentPath - Current pathname
   * @returns {boolean}
   */
  const isCurrentRouteAccessible = (currentPath) => {
    return canAccessRoute(currentPath);
  };

  /**
   * Get redirect path for unauthorized access
   * @param {string} attemptedRoute - Route user tried to access
   * @returns {string}
   */
  const getRedirectPath = (attemptedRoute) => {
    if (attemptedRoute.startsWith('/admin')) {
      return userRole === 'tenant' ? '/tenant-dashboard' : '/admin-dashboard';
    } else if (attemptedRoute.startsWith('/tenant')) {
      return userRole === 'admin' || userRole === 'manager' ? '/admin-dashboard' : '/tenant-dashboard';
    }
    
    return userRole === 'tenant' ? '/tenant-dashboard' : '/admin-dashboard';
  };

  /**
   * Check if user should see specific UI elements
   * @param {string} element - UI element to check
   * @returns {boolean}
   */
  const shouldShowElement = (element) => {
    const elementPermissions = {
      'admin-menu': userRole === 'admin' || userRole === 'manager',
      'settings-menu': userRole === 'admin',
      'user-management': userRole === 'admin',
      'tenant-menu': userRole === 'tenant',
      'maintenance-requests': true, // All users can see maintenance
      'billing-info': userRole === 'tenant',
      'property-management': userRole === 'admin' || userRole === 'manager',
      'lease-management': userRole === 'admin' || userRole === 'manager',
      'invoice-management': userRole === 'admin' || userRole === 'manager'
    };

    return elementPermissions[element] || false;
  };

  return {
    userRole,
    hasRoleAccess,
    canAccessRoute,
    canPerformAction,
    canAccessAdmin,
    canAccessTenant,
    canManageSettings,
    canManageUsers,
    canDelete,
    canCreate,
    canUpdate,
    canRead,
    getAccessibleRoutes,
    isCurrentRouteAccessible,
    getRedirectPath,
    shouldShowElement,
    isAdmin: canAccessAdmin(),
    isTenant: canAccessTenant(),
    isManager: userRole === 'manager'
  };
};
